import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { QaiButtonComponent, QaiIconComponent, QaiInnerWrapComponent } from '@qai/ui';
import { ICertificationAssignment } from 'src/app/interfaces/reportingV2/icertification-assignments';
import { ITeam } from 'src/app/interfaces/reportingV2/iteams';
import { ICertificationAssignmentUserStatsRequest } from 'src/app/interfaces/reportingV2/icertification-assignment-user-stats-request';
import { ICertificationAssignmentUserStatsResponse } from 'src/app/interfaces/reportingV2/icertification-assignment-user-stats-response';
import { IUserCertificationDetail } from 'src/app/interfaces/reportingV2/icertification-users';
import { IIndividualAssignmentEngagementStatsResponse } from 'src/app/interfaces/reportingV2/iindividual-assignment-engagement-stats-response';
import { CertificationAttemptsComponent } from './certification-attempts/certification-attempts.component';
import { CertificationSimulationsComponent } from './certification-simulations/certification-simulations.component';
import { CertificationStatisticsComponent } from './certification-statistics/certification-statistics.component';
import { CertificationUsersComponent } from './certification-users/certification-users.component';
import { CertificationHeaderComponent } from '../certification-header/certification-header.component';
import { CertificationAssignmentUserModalComponent } from "src/app/pages/reporting-dashboard/components/modals/certification-assignment-user-modal/certification-assignment-user-modal.component";
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { IAssignmentViewStatsRequest } from 'src/app/interfaces/reportingV2/iassignment-view-stats-request';
import { CERTIFICATION_STATUS } from 'src/app/utils/constants/reporting-dashboard/certification-status.constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';
import { ReportingNavigationResetService } from 'src/app/services/reporting-dashboard/reporting-navigation-reset.service';

@Component({
  selector: 'app-certification-detail',
  templateUrl: './certification-detail.component.html',
  styleUrls: ['./certification-detail.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    TranslocoModule,
    CommonModule,
    CertificationHeaderComponent,
    CertificationStatisticsComponent,
    CertificationSimulationsComponent,
    CertificationAttemptsComponent,
    CertificationUsersComponent,
    QaiInnerWrapComponent,
    CertificationAssignmentUserModalComponent,
  ],
})
export class CertificationDetailComponent implements OnInit {
  isTranslationLoaded: boolean = false;
  isLoading: boolean = false;
  isUserModalVisible = false;
  certificationId: string | null = null;
  certificationDetails: ICertificationAssignment | null = null;
  certificationTitle: string = '';
  teamOptions: ITeam[] = [];
  selectedTeams: string[] = [];
  isLoadingTeams: boolean = false;
  accountUuid: string | null = null;
  
  // User data properties
  userCertificationData: IUserCertificationDetail[] = [];
  isLoadingUsers: boolean = false;

  @ViewChild('certificationUsersSection', { static: false })
  certificationUsersSection!: ElementRef;

  certificationStatus: string | null = null;
  selectedStatuses: string[] | string = [];

  individualAssignmentEngagementStats: IIndividualAssignmentEngagementStatsResponse | null = null;
  isLoadingEngagementStats: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private dashboardService: ReportingDashboardService,
    private amplitudeService: AmplitudeService,
    private readonly reportingNavigationResetService: ReportingNavigationResetService,
  ) {}

  ngOnInit() {
    this.reportingNavigationResetService.init();
    this.certificationId = this.route.snapshot.paramMap.get('id');
    this.accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    this.certificationStatus = this.route.snapshot.queryParamMap.get(
      'certification_status'
    );
    this.selectedTeams = ReportingLocalStorageUtil.getStoredTeamsFilter(this.accountUuid ?? undefined) ?? [];

    const state = history.state;
    if (state) {
      this.certificationTitle = state['heading'] || '';
    }

    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_INDIVIDUAL_ASSIGNMENT_PAGEVIEW, {
      assignment_name: this.certificationTitle || 'Unknown',
      assignment_type: AssignmentTypeEnum.CERTIFICATION,
    });

    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
    });

    this.loadTeams();
    this.loadUserCertificationData();
    this.loadIndividualAssignmentEngagementStats();
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }

  ngAfterViewInit() {
    if (this.certificationStatus) {
      setTimeout(() => {
        this.scrollToCertificationUsers();
      }, 2000);
    } else {
      this.scrollToTop();
    }
  }

  private scrollToCertificationUsers() {
    if (this.certificationUsersSection) {
      // First, scroll to the element using scrollIntoView
      this.certificationUsersSection.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });

      // Then, after a short delay, ensure it's positioned at the very top
      setTimeout(() => {
        const marginTop = 20;
        const elementTop =
          this.certificationUsersSection.nativeElement.offsetTop - marginTop;
        window.scrollTo({
          top: elementTop,
          behavior: 'smooth',
        });
      }, 200);
    }
  }

  private loadTeams() {
    this.isLoadingTeams = true;
    const accountUuid = this.accountUuid;

    if (!accountUuid || !this.certificationId) {
      console.warn('Missing required data for user engagement API call');
      return;
    }

    this.dashboardService.getTeams(accountUuid, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (teams) => {
        this.teamOptions = teams;
        this.isLoadingTeams = false;
      },
      error: (error) => {
        console.error('Error loading teams:', error);
        this.isLoadingTeams = false;
      },
    });
  }

  private loadUserCertificationData() {
    const accountUuid = this.accountUuid;

    if (!accountUuid || !this.certificationId) {
      console.warn('Missing required data for certification user stats API call');
      return;
    }

    this.isLoadingUsers = true;

    const request: ICertificationAssignmentUserStatsRequest = {
      accountUuid: accountUuid,
      moduleAssignmentUuid: this.certificationId,
      teamUuids: this.selectedTeams,
    };

    this.dashboardService.getCertificationAssignmentUserStats(request, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (response: ICertificationAssignmentUserStatsResponse[]) => {
        this.userCertificationData =
          this.transformApiResponseToUserCertificationDetails(response);
        this.isLoadingUsers = false;

        console.log('userCertificationData', this.userCertificationData);
      },
      error: (error) => {
        console.error('Error loading certification user data:', error);
        this.isLoadingUsers = false;
           },
    });
  }
  private loadIndividualAssignmentEngagementStats() {
    const accountUuid = this.accountUuid;

    if (!accountUuid || !this.certificationId) {
      console.warn('Missing required data for individual assignment engagement stats API call');
      return;
    }

    this.isLoadingEngagementStats = true;

    const teamUuids = this.selectedTeams.includes('all')
      ? []
      : this.selectedTeams;

    const request: IAssignmentViewStatsRequest = {
      accountUuid: accountUuid,
      moduleAssignmentUuid: this.certificationId,
      teamUuids: teamUuids,
    };

    this.dashboardService.getIndividualAssignmentEngagementStatus(request, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (response: IIndividualAssignmentEngagementStatsResponse) => {
        this.individualAssignmentEngagementStats = response;
        this.isLoadingEngagementStats = false;
      },
      error: (error) => {
        console.error('Error loading individual assignment engagement stats:', error);
        this.isLoadingEngagementStats = false;
      },
    });
  }

  onTeamChange(teams: string[]) {
    this.selectedTeams = teams;
    if (this.accountUuid) {
      ReportingLocalStorageUtil.setStoredTeamsFilter(teams, this.accountUuid);
    }
    this.loadIndividualAssignmentEngagementStats();
    this.loadUserCertificationData();
  }

  private transformApiResponseToUserCertificationDetails(
    apiResponse: ICertificationAssignmentUserStatsResponse[]
  ): IUserCertificationDetail[] {
    return apiResponse.map((user, index) => ({
      id: user.uuid || `user-${index}`,
      name: user.userName || '',
      email: user.email || '',
      teams: user.teams
        ? user.teams.split(', ').map((team) => team.trim())
        : [],
      certificationStatus: this.normalizeCertificationStatus(
        user.certificationStatus
      ),
      certifiedOnFirstAttempt: user.certifiedOnFirstAttempt,
      dateOfCertification: user.dateOfCertification || '',
      highScore: user.highScore || '0',
    }));
  }

  private normalizeCertificationStatus(status: string): string {
    const normalizedStatus = status.toLowerCase();
    if (normalizedStatus === CERTIFICATION_STATUS.NOT_STARTED || normalizedStatus === CERTIFICATION_STATUS.ASSIGNED) {
      return this.translocoService.translate('reportingV2.ASSIGNED_LABEL');
    } else if (normalizedStatus === CERTIFICATION_STATUS.IN_PROGRESS) {
      return this.translocoService.translate('reportingV2.IN_PROGRESS_LABEL');
    } else if (normalizedStatus === CERTIFICATION_STATUS.OUT_OF_ATTEMPTS) {
      return this.translocoService.translate('reportingV2.OUT_OF_ATTEMPTS_LABEL');
    } else if (normalizedStatus === CERTIFICATION_STATUS.COMPLETED) {
      return this.translocoService.translate('reportingV2.CERTIFIED_LABEL');
    }
    
    return status;
  }

  goToDashboard() {
    this.router.navigate(['/reporting_dashboard']);
  }

  openUserModal() {
    this.isUserModalVisible = true;
  }

  closeUserModal() {
    this.isUserModalVisible = false;
  }
} 